// Pure presentation for the Radio/TV market index shown on the ads budget
// page. Keeps formatting testable and away from the page component. Prices
// arrive in cents and are already multiplied by the org's effective index.

import { formatCurrencyBRL } from "../../utils/formatCurrencyBRL";

export type MarketIndexLock = { mBps: number; until: string };

export type MarketIndexFeature = {
  month: string; // YYYYMM (UTC)
  c: number; // budget-weighted eligible advertisers per served slot (EMA)
  mBps: number; // published month multiplier, basis points (10000 = 1.00)
  applied: boolean; // false while the index runs in shadow mode (forecast only)
  viewCents: number; // effective price of one view for this org
  accessCents: number; // effective price of one click for this org
  lock: MarketIndexLock | null;
};

export type MarketIndexResp = { radio: MarketIndexFeature; tv: MarketIndexFeature };

export type PacingState = "on_track" | "ahead" | "exhausted" | "unlimited";

// The GET /ads/market-index contract is not deployed yet (Plan 2), so nothing
// has verified the payload the ads service will actually send. Guard the
// shape at the boundary instead of trusting the response type: a missing or
// nested field (for example the wire shape being {effective: {viewCents,
// accessCents}} instead of the flat {viewCents, accessCents} this module
// expects) must hide the block, never render "NaNx" or "R$ NaN". Absence of
// data is "não medido", never a fake value.
export function isMarketIndexFeature(value: unknown): value is MarketIndexFeature {
  if (!value || typeof value !== "object") return false;
  const f = value as Record<string, unknown>;
  if (
    typeof f.month !== "string" ||
    typeof f.c !== "number" ||
    typeof f.mBps !== "number" ||
    typeof f.applied !== "boolean" ||
    typeof f.viewCents !== "number" ||
    typeof f.accessCents !== "number"
  ) {
    return false;
  }
  if (f.lock === null) return true;
  if (!f.lock || typeof f.lock !== "object") return false;
  const lock = f.lock as Record<string, unknown>;
  return typeof lock.mBps === "number" && typeof lock.until === "string";
}

export type MarketIndexView = {
  titleLabel: string;
  multiplierLabel: string;
  contentionLabel: string;
  viewPriceLabel: string;
  accessPriceLabel: string;
  lockLabel: string | null;
  monthLabel: string;
};

const oneDecimal = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const twoDecimals = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatMultiplier(mBps: number): string {
  return `${twoDecimals.format(mBps / 10000)}x`;
}

// Reuses the repo's single shared BRL formatter instead of
// Intl.NumberFormat's currency style, which renders "R$" and the amount with
// a non-breaking space (U+00A0) under full ICU. formatCurrencyBRL keeps a
// plain space and is already the convention ads.tsx uses for money labels.
export function formatCents(cents: number): string {
  return formatCurrencyBRL(cents / 100);
}

function monthLabel(month: string): string {
  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(4, 6)) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11)
    return month;
  return new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, monthIndex, 1)));
}

function lockLabel(lock: MarketIndexLock | null, now: Date): string | null {
  if (!lock) return null;
  const until = new Date(lock.until);
  if (Number.isNaN(until.getTime()) || until.getTime() <= now.getTime()) return null;
  const untilLabel = new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC" }).format(until);
  return `Tarifa de lançamento travada em ${formatMultiplier(lock.mBps)} até ${untilLabel}`;
}

export function buildMarketIndexView(f: MarketIndexFeature, now: Date): MarketIndexView {
  const month = monthLabel(f.month);
  return {
    titleLabel: f.applied
      ? `Tarifa de mercado de ${month}`
      : `Previsão de tarifa de mercado de ${month}`,
    multiplierLabel: formatMultiplier(f.mBps),
    contentionLabel: `${oneDecimal.format(f.c)} anunciantes por intervalo`,
    viewPriceLabel: formatCents(f.viewCents),
    accessPriceLabel: formatCents(f.accessCents),
    lockLabel: lockLabel(f.lock, now),
    monthLabel: month,
  };
}

const pacingCopy: Record<PacingState, string> = {
  on_track: "Orçamento no ritmo do mês",
  ahead: "Orçamento adiantado: a entrega desacelera até o ritmo do mês",
  exhausted: "Limite do mês atingido: anúncios pausados até o próximo mês",
  unlimited: "Sem limite definido",
};

export function pacingLabel(state: PacingState | undefined): string | null {
  if (!state || !Object.hasOwn(pacingCopy, state)) return null;
  return pacingCopy[state];
}
